import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Headphones,
  Lock,
  MessageCircle,
  ShoppingBag,
  Sparkles,
  Store,
} from 'lucide-react';
import { authApi, channelsApi, storefrontAdminApi } from '@/services/api';
import ShopifyIntegrationTab from '@/components/settings/ShopifyIntegrationTab';
import WhatsAppShopTab from '@/components/settings/WhatsAppShopTab';
import EbayIntegrationTab from '@/components/settings/EbayIntegrationTab';
import AmazonIntegrationTab from '@/components/settings/AmazonIntegrationTab';
import MetaShopsIntegrationTab from '@/components/settings/MetaShopsIntegrationTab';
import TikTokShopIntegrationTab from '@/components/settings/TikTokShopIntegrationTab';
import OnBuyIntegrationTab from '@/components/settings/OnBuyIntegrationTab';

type ConfigureId = 'shopify' | 'whatsapp' | 'ebay' | 'amazon' | 'onbuy' | 'meta' | 'tiktok';

type CardDef = {
  id: string;
  title: string;
  blurb: string;
  icon: typeof ShoppingBag;
  available: boolean;
  unlocked: boolean;
  configure?: ConfigureId;
  href?: string;
  supportSubject?: string;
  featureKey?: string;
  maturity?: string;
  canConfigure?: boolean;
  connected?: boolean;
};

const ICON_BY_ID: Record<string, typeof ShoppingBag> = {
  'public-store': Store,
  storefront: Store,
  shopify: ShoppingBag,
  whatsapp: MessageCircle,
  ai: Sparkles,
  ebay: ShoppingBag,
  amazon: ShoppingBag,
  onbuy: ShoppingBag,
  meta: ShoppingBag,
  tiktok: ShoppingBag,
};

/**
 * Marketplace hub — Channel Manager driven cards; unlock from Active add-ons.
 * Responsive grid; scaffold channels open a secure help panel (no credential forms).
 */
export default function MarketplaceChannelsTab() {
  const [params, setParams] = useSearchParams();
  const configure = params.get('configure') as ConfigureId | null;

  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => (await authApi.getCompanyProfile()).data.message,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const { data: storefrontSettings } = useQuery({
    queryKey: ['storefrontSettings', 'marketplace'],
    queryFn: async () => (await storefrontAdminApi.getSettings()).data.message,
    staleTime: 60_000,
  });

  const { data: channelPayload } = useQuery({
    queryKey: ['channels', 'marketplace'],
    queryFn: async () => (await channelsApi.listChannels()).data.message as {
      channels: Array<Record<string, unknown>>;
    },
    staleTime: 60_000,
    retry: 1,
  });

  const features: string[] = Array.isArray(profile?.features) ? profile.features : [];
  const addonCodes: string[] = (profile?.selected_add_ons || [])
    .map((a: string | { status?: string; feature_key?: string; addon_code?: string; name?: string }) => {
      if (typeof a === 'string') return a.toLowerCase();
      if (a?.status && a.status !== 'Active') return '';
      return String(a?.feature_key || a?.addon_code || a?.name || '').toLowerCase();
    })
    .filter((s: string) => Boolean(s));

  const isWhatsappAllowed = Boolean(profile?.allow_whatsapp) || features.includes('allow_whatsapp');
  const isShopifyAllowed = Boolean(profile?.allow_shopify) || features.includes('allow_shopify');
  const isEbayAllowed = Boolean(profile?.allow_ebay) || features.includes('allow_ebay');
  const isAmazonAllowed =
    Boolean(profile?.allow_amazon) ||
    features.includes('allow_amazon') ||
    addonCodes.includes('allow_amazon');
  const isMetaAllowed =
    Boolean(profile?.allow_meta) ||
    features.includes('allow_meta') ||
    addonCodes.includes('allow_meta');
  const isTiktokAllowed =
    Boolean(profile?.allow_tiktok) ||
    features.includes('allow_tiktok') ||
    addonCodes.includes('allow_tiktok');
  const isOnbuyAllowed =
    Boolean(profile?.allow_onbuy) ||
    features.includes('allow_onbuy') ||
    addonCodes.includes('allow_onbuy');
  const isWebAllowed =
    Boolean(profile?.allow_web) ||
    features.includes('allow_web') ||
    Boolean(storefrontSettings?.allow_web) ||
    addonCodes.some((c: string) => c === 'allow_web' || c.includes('web') || c.includes('storefront'));
  const isAiAllowed =
    Boolean(profile?.allow_ai_analytics) ||
    features.includes('ai_analytics') ||
    addonCodes.some((c: string) => c === 'ai_analytics' || c.includes('ai'));
  const storefrontOn = Boolean(storefrontSettings?.enable_storefront) && isWebAllowed;

  const setConfigure = (id: ConfigureId | null) => {
    const next = new URLSearchParams(params);
    next.set('tab', 'marketplaces');
    if (id) next.set('configure', id);
    else next.delete('configure');
    setParams(next, { replace: true });
  };

  const cards: CardDef[] = useMemo(() => {
    const fromApi = (channelPayload?.channels || []).map((ch) => {
      const id = String(ch.id === 'storefront' ? 'public-store' : ch.id);
      const maturity = String(ch.maturity || 'scaffold');
      const unlocked = Boolean(ch.unlocked);
      const available = maturity === 'live';
      const status = (ch.status || {}) as { connected?: boolean };
      return {
        id,
        title: String(ch.title || id),
        blurb: String(ch.blurb || ''),
        icon: ICON_BY_ID[id] || ShoppingBag,
        available,
        unlocked,
        configure: (ch.configure as ConfigureId) || undefined,
        href: ch.href ? String(ch.href) : undefined,
        supportSubject: ch.support_subject ? String(ch.support_subject) : undefined,
        featureKey: ch.feature_key ? String(ch.feature_key) : undefined,
        maturity,
        canConfigure: Boolean(ch.can_configure),
        connected: Boolean(status.connected),
      } satisfies CardDef;
    });

    // Always show WhatsApp + AI (not yet in Channel Manager catalogue)
    const extras: CardDef[] = [
      {
        id: 'whatsapp',
        title: 'WhatsApp Shop',
        blurb: 'Catalogue and checkout over WhatsApp Business — orders in Marketplace Orders.',
        icon: MessageCircle,
        available: true,
        unlocked: isWhatsappAllowed,
        configure: 'whatsapp',
        supportSubject: 'Enable WhatsApp Shop',
        featureKey: 'allow_whatsapp',
        maturity: 'live',
        canConfigure: isWhatsappAllowed,
      },
      {
        id: 'ai',
        title: 'AI Analytics',
        blurb: 'AI-driven sales forecasts and customer insights for your POS.',
        icon: Sparkles,
        available: true,
        unlocked: isAiAllowed,
        supportSubject: 'Enable AI Analytics',
        featureKey: 'ai_analytics',
        maturity: 'live',
      },
    ];

    if (fromApi.length) {
      // Prefer API catalogue; inject WhatsApp/AI after storefront/shopify
      const ids = new Set(fromApi.map((c) => c.id));
      const merged: CardDef[] = [...fromApi];
      for (const e of extras) {
        if (!ids.has(e.id)) {
          const after = merged.findIndex((c) => c.id === 'shopify');
          merged.splice(after >= 0 ? after + 1 : merged.length, 0, e);
        }
      }
      // Align unlock for live channels from profile mirrors when API lags
      return merged.map((c) => {
        if (c.id === 'public-store' || c.id === 'storefront') return { ...c, unlocked: c.unlocked || isWebAllowed };
        if (c.id === 'shopify') return { ...c, unlocked: c.unlocked || isShopifyAllowed };
        if (c.id === 'ebay') {
          return {
            ...c,
            unlocked: c.unlocked || isEbayAllowed,
            available: true,
            maturity: 'live',
            canConfigure: c.unlocked || isEbayAllowed,
          };
        }
        if (c.id === 'amazon') {
          return {
            ...c,
            unlocked: c.unlocked || isAmazonAllowed,
            available: true,
            maturity: 'live',
            canConfigure: c.unlocked || isAmazonAllowed,
          };
        }
        if (c.id === 'meta') {
          return {
            ...c,
            unlocked: c.unlocked || isMetaAllowed,
            available: true,
            maturity: 'live',
            canConfigure: c.unlocked || isMetaAllowed,
          };
        }
        if (c.id === 'tiktok') {
          return {
            ...c,
            unlocked: c.unlocked || isTiktokAllowed,
            available: true,
            maturity: 'live',
            canConfigure: c.unlocked || isTiktokAllowed,
          };
        }
        if (c.id === 'onbuy') {
          return {
            ...c,
            unlocked: c.unlocked || isOnbuyAllowed,
            available: true,
            maturity: 'live',
            canConfigure: c.unlocked || isOnbuyAllowed,
          };
        }
        return c;
      });
    }

    // Offline fallback (API not migrated yet)
    return [
      {
        id: 'public-store',
        title: 'Public Store',
        blurb: 'Your own online shop — themes, delivery zones, pages, domain and Stripe checkout.',
        icon: Store,
        available: true,
        unlocked: isWebAllowed,
        href: '/public-store',
        supportSubject: 'Enable Public Store (Web Integration)',
        featureKey: 'allow_web',
        maturity: 'live',
      },
      {
        id: 'shopify',
        title: 'Shopify',
        blurb: 'Sync catalogue and pull Shopify orders into Marketplace Orders.',
        icon: ShoppingBag,
        available: true,
        unlocked: isShopifyAllowed,
        configure: 'shopify',
        supportSubject: 'Enable Shopify integration',
        featureKey: 'allow_shopify',
        maturity: 'live',
        canConfigure: isShopifyAllowed,
      },
      ...extras,
      {
        id: 'ebay',
        title: 'eBay',
        blurb: 'List on eBay UK and accept orders in Marketplace Orders.',
        icon: ShoppingBag,
        available: true,
        unlocked: isEbayAllowed,
        configure: 'ebay',
        supportSubject: 'Enable eBay integration',
        maturity: 'live',
        canConfigure: isEbayAllowed,
      },
      {
        id: 'amazon',
        title: 'Amazon',
        blurb: 'Connect Seller Central to list products and import marketplace orders.',
        icon: ShoppingBag,
        available: true,
        unlocked: isAmazonAllowed,
        configure: 'amazon',
        supportSubject: 'Enable Amazon integration',
        maturity: 'live',
        canConfigure: isAmazonAllowed,
      },
      {
        id: 'onbuy',
        title: 'OnBuy',
        blurb: 'UK marketplace — sync listings, stock and orders.',
        icon: ShoppingBag,
        available: true,
        unlocked: isOnbuyAllowed,
        configure: 'onbuy',
        supportSubject: 'Enable OnBuy integration',
        maturity: 'live',
        canConfigure: isOnbuyAllowed,
      },
      {
        id: 'meta',
        title: 'Meta Shops',
        blurb: 'Facebook & Instagram Shops — catalogue, stock and social commerce orders.',
        icon: ShoppingBag,
        available: true,
        unlocked: isMetaAllowed,
        configure: 'meta',
        supportSubject: 'Enable Meta Shops integration',
        maturity: 'live',
        canConfigure: isMetaAllowed,
      },
      {
        id: 'tiktok',
        title: 'TikTok Shop',
        blurb: 'TikTok Shop UK — list products, sync stock and pull orders.',
        icon: ShoppingBag,
        available: true,
        unlocked: isTiktokAllowed,
        configure: 'tiktok',
        supportSubject: 'Enable TikTok Shop integration',
        maturity: 'live',
        canConfigure: isTiktokAllowed,
      },
    ];
  }, [
    channelPayload,
    isShopifyAllowed,
    isEbayAllowed,
    isAmazonAllowed,
    isMetaAllowed,
    isTiktokAllowed,
    isOnbuyAllowed,
    isWhatsappAllowed,
    isWebAllowed,
    isAiAllowed,
    features,
  ]);

  if (configure === 'shopify' && isShopifyAllowed) {
    return (
      <div className="space-y-4 min-w-0">
        <button
          type="button"
          onClick={() => setConfigure(null)}
          className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Marketplaces
        </button>
        <ShopifyIntegrationTab />
      </div>
    );
  }

  if (configure === 'whatsapp' && isWhatsappAllowed) {
    return (
      <div className="space-y-4 min-w-0">
        <button
          type="button"
          onClick={() => setConfigure(null)}
          className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Marketplaces
        </button>
        <WhatsAppShopTab />
      </div>
    );
  }

  if (configure === 'ebay' && isEbayAllowed) {
    return (
      <div className="space-y-4 min-w-0">
        <button
          type="button"
          onClick={() => setConfigure(null)}
          className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Marketplaces
        </button>
        <EbayIntegrationTab />
      </div>
    );
  }

  if (configure === 'amazon' && isAmazonAllowed) {
    return (
      <div className="space-y-4 min-w-0">
        <button
          type="button"
          onClick={() => setConfigure(null)}
          className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Marketplaces
        </button>
        <AmazonIntegrationTab />
      </div>
    );
  }

  if (configure === 'meta' && isMetaAllowed) {
    return (
      <div className="space-y-4 min-w-0">
        <button
          type="button"
          onClick={() => setConfigure(null)}
          className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Marketplaces
        </button>
        <MetaShopsIntegrationTab />
      </div>
    );
  }

  if (configure === 'tiktok' && isTiktokAllowed) {
    return (
      <div className="space-y-4 min-w-0">
        <button
          type="button"
          onClick={() => setConfigure(null)}
          className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Marketplaces
        </button>
        <TikTokShopIntegrationTab />
      </div>
    );
  }

  if (configure === 'onbuy' && isOnbuyAllowed) {
    return (
      <div className="space-y-4 min-w-0">
        <button
          type="button"
          onClick={() => setConfigure(null)}
          className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Marketplaces
        </button>
        <OnBuyIntegrationTab />
      </div>
    );
  }

  return (
    <div className="space-y-6 min-w-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Marketplace</h2>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Connect sales channels from one place. Package tiers cover POS limits — channel integrations are
            add-ons. New marketplaces plug into the same Channel Manager as they go live.
          </p>
        </div>
        <Link
          to="/channel-pricing"
          className="text-sm font-medium text-teal-700 hover:underline shrink-0"
        >
          Channel pricing →
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
        {cards.map((c) => {
          const Icon = c.icon;
          const locked = !c.unlocked;
          const comingSoon = c.maturity === 'scaffold' || (!c.available && !c.unlocked);
          const liveUnlocked = c.available && c.unlocked;

          const enabledBadge =
            (c.id === 'public-store' || c.id === 'storefront') && storefrontOn
              ? 'Enabled'
              : c.id === 'shopify' &&
                  c.unlocked &&
                  (profile?.enable_shopify === true || profile?.enable_shopify === 1 || c.connected)
                ? 'Enabled'
                : c.id === 'whatsapp' &&
                    c.unlocked &&
                    (profile?.enable_whatsapp_shop === true || profile?.enable_whatsapp_shop === 1)
                  ? 'Enabled'
                  : c.connected
                    ? 'Connected'
                    : null;

          let statusLabel = 'Coming soon';
          let statusClass = 'bg-gray-100 text-gray-600';
          if (liveUnlocked) {
            statusLabel = enabledBadge || 'Included';
            statusClass = enabledBadge ? 'bg-emerald-50 text-emerald-700' : 'bg-sky-50 text-sky-700';
          } else if (locked && c.maturity === 'scaffold') {
            statusLabel = 'Coming soon';
            statusClass = 'bg-gray-100 text-gray-600';
          } else if (locked) {
            statusLabel = 'Add-on';
            statusClass = 'bg-amber-50 text-amber-800';
          } else if (c.maturity === 'scaffold' && c.unlocked) {
            statusLabel = 'Early access';
            statusClass = 'bg-violet-50 text-violet-800';
          }

          const supportHref = `/support${
            c.supportSubject ? `?subject=${encodeURIComponent(c.supportSubject)}` : ''
          }`;

          return (
            <div
              key={c.id}
              className={`bg-white border rounded-xl p-4 sm:p-5 flex flex-col gap-3 min-w-0 ${
                locked && c.available ? 'border-amber-200/80' : 'border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div
                    className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                      locked && c.available ? 'bg-amber-50' : 'bg-gray-100'
                    }`}
                  >
                    {locked && c.available ? (
                      <Lock className="w-4 h-4 text-amber-700" />
                    ) : (
                      <Icon className="w-4 h-4 text-gray-600" />
                    )}
                  </div>
                  <h3 className="font-semibold text-gray-900 truncate">{c.title}</h3>
                </div>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ${statusClass}`}>
                  {statusLabel}
                </span>
              </div>
              <p className="text-sm text-gray-600 flex-1">{c.blurb}</p>

              {locked && c.available && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                  Not included in your current package. Contact Support to purchase and enable this
                  integration.
                </p>
              )}

              {comingSoon && (
                <p className="text-xs text-gray-500">
                  Scaffolded in Channel Manager — ask Support for early access when Connect goes live.
                </p>
              )}

              <div className="flex flex-wrap gap-2 mt-auto">
                {liveUnlocked && c.href ? (
                  <Link to={c.href} className="btn-primary text-sm w-fit">
                    {enabledBadge ? 'Manage' : 'Open'}
                  </Link>
                ) : null}

                {liveUnlocked && c.configure ? (
                  <button
                    type="button"
                    onClick={() => setConfigure(c.configure!)}
                    className="btn-primary text-sm w-fit"
                  >
                    Configure
                  </button>
                ) : null}

                {c.maturity === 'scaffold' && c.configure ? (
                  <button
                    type="button"
                    onClick={() => setConfigure(c.configure!)}
                    className="btn-secondary text-sm w-fit"
                  >
                    Learn more
                  </button>
                ) : null}

                {liveUnlocked && !c.href && !c.configure ? (
                  <Link to={supportHref} className="btn-secondary text-sm w-fit">
                    Contact Support
                  </Link>
                ) : null}

                {(locked && c.available) || (comingSoon && !c.configure) ? (
                  <Link
                    to={supportHref}
                    className="btn-secondary text-sm inline-flex items-center gap-1.5 w-fit"
                  >
                    <Headphones className="w-3.5 h-3.5" />
                    Contact Support
                  </Link>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
